import * as React from 'react';

import { useUIConfig } from '~/providers/ui-config-context';
import { CookieHelper } from '~/services/actions/cookie-actions';
import { Devices, type IDevice, MinScreenSizes } from '~/types/device';

// stores
export { Devices, MinScreenSizes };

function useMedia(): {
  device: keyof typeof Devices;
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
} {
  const { deviceType } = useUIConfig();
  const serverDevice = deviceType.toLowerCase() as keyof typeof Devices;

  const cookieHelper = React.useMemo(() => new CookieHelper(), []);

  const timerRef = React.useRef<NodeJS.Timeout>();
  const scrollTopRef = React.useRef<number>(0);

  const [reactClientDevice, setReactClientDevice] = React.useState<IDevice>();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      if (!width) return;

      // Capture scroll position
      scrollTopRef.current = window.scrollY;

      // Calculate new device
      const newDevice = ((): IDevice => {
        const { desktop, tablet } = MinScreenSizes;

        switch (true) {
          case width < tablet:
            return Devices.mobile;
          case width < desktop:
            return Devices.tablet;
          default:
            return Devices.desktop;
        }
      })();

      setReactClientDevice(newDevice);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (!reactClientDevice) return;

    const ONE_YEAR = 365 * 24 * 60 * 60 * 1000;
    const time = new Date().getTime() + ONE_YEAR;

    cookieHelper.setBrowserCookie('device', reactClientDevice, time);
  }, [reactClientDevice, cookieHelper]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || process.env.NODE_ENV === 'production') {
      return;
    }

    // Resolve tablet spacing issues on resize
    timerRef.current = setTimeout(() => {
      if (!window.location.hash) {
        window.scrollTo({ top: scrollTopRef.current });
      }

      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }, 0);
  }, [reactClientDevice]);

  const device = reactClientDevice || serverDevice;
  const isMobile = device === Devices.mobile;
  const isTablet = device === Devices.tablet;
  const isDesktop = device === Devices.desktop;

  return {
    device,
    isMobile,
    isTablet,
    isDesktop,
  };
}

export default useMedia;
